/**
 * Referral Storage Utilities
 *
 * Provides safe, reliable storage and retrieval of referral codes using sessionStorage.
 * Includes graceful handling of sessionStorage unavailability and comprehensive error logging.
 */

const REFERRAL_CODE_KEY = "referral_code";

/**
 * Stores a referral code in sessionStorage with error handling
 * @param token The referral code to store
 */
export function setReferralCode(token: string): void {
  try {
    if (typeof window === "undefined") {
      console.warn(
        "[ReferralStorage] setReferralCode: Window is undefined (server-side context)",
      );
      return;
    }

    if (!window.sessionStorage) {
      console.warn(
        "[ReferralStorage] setReferralCode: sessionStorage is not available",
      );
      return;
    }

    window.sessionStorage.setItem(REFERRAL_CODE_KEY, token);
  } catch (error) {
    console.error(
      "[ReferralStorage] setReferralCode: Failed to store referral code",
      {
        error: error instanceof Error ? error.message : "Unknown error",
        token: token?.substring(0, 10) + "...", // Log partial token for debugging
      },
    );
  }
}

/**
 * Retrieves a referral code from sessionStorage with error handling
 * @returns The stored referral code or null if not found/unavailable
 */
export function getReferralCode(): string | null {
  try {
    if (typeof window === "undefined") {
      console.warn(
        "[ReferralStorage] getReferralCode: Window is undefined (server-side context)",
      );
      return null;
    }

    if (!window.sessionStorage) {
      console.warn(
        "[ReferralStorage] getReferralCode: sessionStorage is not available",
      );
      return null;
    }

    const token = window.sessionStorage.getItem(REFERRAL_CODE_KEY);
    return token;
  } catch (error) {
    console.error(
      "[ReferralStorage] getReferralCode: Failed to retrieve referral code",
      {
        error: error instanceof Error ? error.message : "Unknown error",
      },
    );
    return null;
  }
}

/**
 * Clears the stored referral code from sessionStorage with error handling
 */
export function clearReferralCode(): void {
  try {
    if (typeof window === "undefined") {
      console.warn(
        "[ReferralStorage] clearReferralCode: Window is undefined (server-side context)",
      );
      return;
    }

    if (!window.sessionStorage) {
      console.warn(
        "[ReferralStorage] clearReferralCode: sessionStorage is not available",
      );
      return;
    }

    window.sessionStorage.removeItem(REFERRAL_CODE_KEY);
  } catch (error) {
    console.error(
      "[ReferralStorage] clearReferralCode: Failed to clear referral code",
      {
        error: error instanceof Error ? error.message : "Unknown error",
      },
    );
  }
}

/**
 * Checks if sessionStorage is available for use
 * @returns true if sessionStorage is available, false otherwise
 */
export function isSessionStorageAvailable(): boolean {
  try {
    if (typeof window === "undefined") {
      return false;
    }

    if (!window.sessionStorage) {
      return false;
    }

    // Test if we can actually write to sessionStorage
    const testKey = "__referral_storage_test__";
    window.sessionStorage.setItem(testKey, "test");
    window.sessionStorage.removeItem(testKey);
    return true;
  } catch (error) {
    console.warn(
      "[ReferralStorage] isSessionStorageAvailable: sessionStorage test failed",
      {
        error: error instanceof Error ? error.message : "Unknown error",
      },
    );
    return false;
  }
}
